import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { 
  StarIcon,
  ArrowDownTrayIcon,
  HeartIcon,
  EyeIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils';
import { productService, Product } from '../services/productService';
import { purchaseService } from '../services/purchaseService';
import { favoriteService } from '../services/favoriteService';
import { reviewService, Review } from '../services/reviewService';
import { useAuth } from '../context/AuthContext';
import { awsService } from '../services/awsService';
import { NotificationModal, NotificationType } from '../components/ui/NotificationModal';
import { ConfirmModal } from '../components/ui/ConfirmModal';

const CATEGORY_LABELS: Record<string, string> = {
  POLICY_PACKS: 'Policy Packs',
  RISK_ASSESSMENTS: 'Risk Assessments',
  CARE_PLANS: 'Care Plans',
  HR_RECRUITMENT: 'HR & Recruitment',
  QUALITY_ASSURANCE: 'Quality Assurance',
  INCIDENT_REPORTING: 'Incident Reporting',
  TRAINING_INDUCTION: 'Training & Induction',
  FORMS_CHECKLISTS: 'Forms & Checklists',
  GOVERNANCE_LEADERSHIP: 'Governance & Leadership',
  FINANCE_BUSINESS: 'Finance & Business',
};

const SECTOR_LABELS: Record<string, string> = {
  CHILDRENS_HOMES: "Children's Homes",
  SUPPORTED_ACCOMMODATION: 'Supported Accommodation',
  SUPPORTED_LIVING: 'Supported Living',
  DOMICILIARY_CARE: 'Domiciliary Care',
  ADULT_RESIDENTIAL: 'Adult Residential',
  FOSTERING_AGENCIES: 'Fostering Agencies',
  DAY_CENTRES: 'Day Centres',
  SPECIALIST_SEN: 'Specialist SEN',
  MENTAL_HEALTH: 'Mental Health',
};

export const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  
  // Notification modal state
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    type: NotificationType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  // Confirmation modal state
  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const showNotification = (type: NotificationType, title: string, message: string) => {
    setNotification({ isOpen: true, type, title, message });
  };

  const loadProduct = useCallback(async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const productData = await productService.getProduct(id);
      setProduct(productData);
      // Set selected image to thumbnail or first gallery image (using signed URLs)
      const thumbnail = productData.thumbnailUrl 
        || productData.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL')?.signedUrl
        || productData.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL')?.fileUrl;
      
      if (thumbnail) {
        setSelectedImage(thumbnail);
      } else if (productData.media && productData.media.length > 0) {
        // Use first media's signed URL
        const firstMedia = productData.media[0];
        setSelectedImage(firstMedia.signedUrl || firstMedia.fileUrl);
      }

      // Check if user has purchased this product
      if (user) {
        try {
          const purchases = await purchaseService.getMyPurchases();
          const userPurchase = purchases.find(
            (p) => p.product.id === productData.id && p.paymentStatus === 'PAID'
          );
          if (userPurchase) {
            setHasPurchased(true);
          }
        } catch (error) {
          // Silently fail - user might not have any purchases
          console.error('Error checking purchases:', error);
        }

        // Check if product is favorited
        try {
          const favorited = await favoriteService.checkIfFavorited(productData.id);
          setIsFavorited(favorited);
        } catch (error) {
          console.error('Error checking favorites:', error);
        }
      }

    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, user]);

  const loadReviews = useCallback(async (productId: string) => {
    setIsLoadingReviews(true);
    try {
      const [allReviews, userReview] = await Promise.all([
        reviewService.getProductReviews(productId),
        user ? reviewService.getMyReview(productId).catch(() => null) : Promise.resolve(null),
      ]);
      setReviews(allReviews);
      setMyReview(userReview);
      if (userReview) {
        setReviewForm({ rating: userReview.rating, comment: userReview.comment || '' });
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setIsLoadingReviews(false);
    }
  }, [user]);

  useEffect(() => {
    if (product?.id) {
      loadReviews(product.id);
    }
  }, [product?.id, loadReviews]);

  useEffect(() => {
    if (id) {
      loadProduct();
    }
  }, [id, loadProduct]);

  const getImageUrl = (media: any): string => {
    // Prefer signed URL if available, otherwise use fileUrl or construct from key
    if (media?.signedUrl) return media.signedUrl;
    if (media?.fileUrl) return media.fileUrl;
    if (typeof media === 'string') {
      // Fallback: if media is just a key string
      return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${media}`;
    }
    return '';
  };

  const getGalleryImages = (): string[] => {
    if (!product) return [];
    
    const images: string[] = [];
    
    // Add thumbnail with signed URL
    if (product.thumbnailUrl) {
      images.push(product.thumbnailUrl);
    } else if (product.thumbnail) {
      const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
      if (thumbnailMedia) {
        images.push(getImageUrl(thumbnailMedia));
      }
    }
    
    // Add gallery images with signed URLs
    const galleryMedia = product.media?.filter(m => m.entityType === 'PRODUCT_GALLERY') || [];
    galleryMedia.forEach(media => {
      const url = getImageUrl(media);
      if (url) images.push(url);
    });
    
    return images;
  };

  const handlePurchase = async () => {
    if (!product || !user) {
      navigate('/login');
      return;
    }

    // Check if already purchased
    if (hasPurchased) {
      handleDownload();
      return;
    }

    setIsPurchasing(true);
    try {
      // Create Stripe checkout session
      const checkoutSession = await purchaseService.createCheckoutSession(product.id);
      
      // Redirect to Stripe Checkout
      if (checkoutSession.url) {
        window.location.href = checkoutSession.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate purchase. Please try again.';
      
      // Check if error is related to seller's Stripe account
      if (errorMessage.includes('Stripe') || errorMessage.includes('payment account') || errorMessage.includes('seller')) {
        showNotification(
          'error', 
          'Payment Setup Required', 
          'The seller needs to complete their payment account setup. Please try again later or contact support.'
        );
      } else {
        showNotification('error', 'Purchase Failed', errorMessage);
      }
      setIsPurchasing(false);
    }
  };

  const handleDownload = async () => {
    if (!product || !user) {
      navigate('/login');
      return;
    }

    // Check if user has purchased (unless they're the seller)
    if (product.sellerId !== user.id && !hasPurchased) {
      showNotification('warning', 'Purchase Required', 'Please purchase this product to download it.');
      return;
    }

    setIsDownloading(true);
    try {
      // Generate download URL
      const { downloadUrl } = await awsService.generateDownloadUrl(product.fileUrl);
      
      // Track download
      await productService.incrementDownloads(product.id);
      
      // Open download in new tab
      window.open(downloadUrl, '_blank');
      
      // Reload product to update download count
      await loadProduct();
    } catch (error) {
      console.error('Error downloading:', error);
      showNotification('error', 'Download Failed', 'Failed to download file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreview = () => {
    if (product?.previewSignedUrl) {
      window.open(product.previewSignedUrl, '_blank');
    } else if (product?.previewUrl) {
      // Fallback: try to get signed URL
      const previewMedia = product.media?.find(m => m.entityType === 'PRODUCT_PREVIEW');
      if (previewMedia?.signedUrl) {
        window.open(previewMedia.signedUrl, '_blank');
      } else if (previewMedia?.fileUrl) {
        window.open(previewMedia.fileUrl, '_blank');
      }
    }
  };

  const handleToggleFavorite = async () => {
    if (!product || !user) {
      navigate('/login');
      return;
    }

    setIsTogglingFavorite(true);
    try {
      if (isFavorited) {
        await favoriteService.removeFromFavorites(product.id);
        setIsFavorited(false);
      } else {
        await favoriteService.addToFavorites(product.id);
        setIsFavorited(true);
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      showNotification('error', 'Favorite Update Failed', error.message || 'Failed to update favorite. Please try again.');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!product || !user) {
      navigate('/login');
      return;
    }

    if (reviewForm.rating < 1 || reviewForm.rating > 5) {
      showNotification('warning', 'Invalid Rating', 'Please select a rating between 1 and 5 stars');
      return;
    }

    setIsSubmittingReview(true);
    try {
      if (myReview) {
        // Update existing review
        await reviewService.updateReview(myReview.id, reviewForm);
      } else {
        // Create new review
        await reviewService.createReview(product.id, reviewForm);
      }
      
      // Reload reviews and product
      await Promise.all([
        loadReviews(product.id),
        loadProduct(),
      ]);
      
      setShowReviewForm(false);
      showNotification('success', 'Review Submitted', myReview ? 'Review updated successfully!' : 'Review submitted successfully!');
    } catch (error: any) {
      console.error('Error submitting review:', error);
      showNotification('error', 'Review Submission Failed', error.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleDeleteReview = () => {
    if (!myReview) return;

    setConfirmation({
      isOpen: true,
      title: 'Delete Review',
      message: 'Are you sure you want to delete your review? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await reviewService.deleteReview(myReview.id);
          await Promise.all([
            loadReviews(product!.id),
            loadProduct(),
          ]);
          setMyReview(null);
          setReviewForm({ rating: 5, comment: '' });
          showNotification('success', 'Review Deleted', 'Review deleted successfully!');
        } catch (error: any) {
          console.error('Error deleting review:', error);
          showNotification('error', 'Delete Failed', error.message || 'Failed to delete review. Please try again.');
        }
      },
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">
            Product Not Found
          </h1>
          <p className="text-secondary-600 mb-8">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/marketplace')}>
            Browse Marketplace
          </Button>
        </div>
      </div>
    );
  }

  const galleryImages = getGalleryImages();
  const mainImage = selectedImage || galleryImages[0] || 'https://via.placeholder.com/800x600?text=No+Image';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-secondary-600 hover:text-primary-600 mb-6 transition-colors"
      >
        <ChevronLeftIcon className="h-5 w-5 mr-1" />
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Image Gallery */}
          <Card>
            <CardContent className="p-0">
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden rounded-t-lg">
                  <img
                    src={mainImage}
              alt={product.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      // Try fallback if signed URL fails
                      if (product.thumbnailUrl && target.src !== 'https://via.placeholder.com/800x600?text=No+Image') {
                        target.src = product.media?.[0]?.fileUrl || 'https://via.placeholder.com/800x600?text=No+Image';
                      } else {
                        target.src = 'https://via.placeholder.com/800x600?text=No+Image';
                      }
                    }}
            />
          </div>
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <Badge 
                    variant={
                      product.status === 'APPROVED' ? 'success' :
                      product.status === 'PENDING' ? 'warning' :
                      'error'
                    }
                  >
                    {product.status}
                  </Badge>
                </div>
              </div>

              {/* Thumbnail Gallery */}
              {galleryImages.length > 1 && (
                <div className="p-4 grid grid-cols-4 sm:grid-cols-6 gap-2 border-t border-secondary-200">
                  {galleryImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(image)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === image
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-secondary-200 hover:border-primary-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=No+Image';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Product Details */}
          <Card>
            <CardContent className="p-6 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Badge variant="primary">
                        {CATEGORY_LABELS[product.category] || product.category}
                      </Badge>
                      <Badge variant="secondary">
                        {SECTOR_LABELS[product.sector] || product.sector}
                      </Badge>
                    </div>
                    <h1 className="text-3xl font-bold text-secondary-900 mb-3">
                      {product.title}
                    </h1>
                    <div className="flex items-center gap-6 text-sm text-secondary-600">
                      <div className="flex items-center">
                        <StarIcon className="h-5 w-5 text-yellow-400 fill-current mr-1" />
                        <span className="font-medium">
                          {product.rating > 0 ? product.rating.toFixed(1) : '0.0'}
                        </span>
                        <span className="ml-1">
                          ({product.reviewCount} {product.reviewCount === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                      <div className="flex items-center">
                        <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
                        {product.downloads} downloads
                      </div>
                      <div className="text-secondary-500">
                        Added {new Date(product.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleFavorite}
                    disabled={isTogglingFavorite || !user}
                    className={`p-3 rounded-full transition-colors ml-4 ${
                      isFavorited
                        ? 'text-red-500 bg-red-50'
                        : 'text-secondary-400 hover:text-red-500 hover:bg-red-50'
                    } ${isTogglingFavorite ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={user ? (isFavorited ? 'Remove from favorites' : 'Add to favorites') : 'Login to add to favorites'}
                  >
                    <HeartIcon className={`h-6 w-6 ${isFavorited ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-semibold text-secondary-900 mb-3">
                  Description
                </h2>
                <p className="text-secondary-600 leading-relaxed whitespace-pre-line">
                  {product.description}
                </p>
              </div>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
              )}

              {/* Seller Information */}
              {product.seller && (
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-3">
                  Seller Information
                </h3>
                  <button
                    onClick={() => navigate(`/seller/${product.seller.id}`)}
                    className="w-full flex items-center space-x-4 p-4 bg-gradient-to-r from-secondary-50 to-primary-50 rounded-lg border border-secondary-200 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer text-left"
                  >
                    <div className="h-14 w-14 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                      {product.seller.avatar ? (
                  <img
                    src={product.seller.avatar}
                    alt={product.seller.name}
                          className="h-full w-full rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xl font-bold">
                          {product.seller.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-secondary-900 truncate hover:text-primary-600 transition-colors">
                        {product.seller.name || 'Unknown Seller'}
                    </h4>
                      <p className="text-sm text-secondary-600 truncate">
                      {product.seller.email}
                    </p>
                    <div className="flex items-center mt-1">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1 flex-shrink-0" />
                      <span className="text-sm text-green-600">Verified Seller</span>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Reviews Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-secondary-900">
                    Reviews ({reviews.length})
                  </h3>
                  {user && product.sellerId !== user.id && !myReview && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowReviewForm(!showReviewForm)}
                    >
                      {showReviewForm ? 'Cancel' : 'Write a Review'}
                    </Button>
                  )}
                </div>

                {/* Review Form */}
                {showReviewForm && user && product.sellerId !== user.id && (
                  <Card className="mb-6">
                    <CardContent className="p-6">
                      <h4 className="font-semibold text-secondary-900 mb-4">
                        {myReview ? 'Update Your Review' : 'Write a Review'}
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">
                            Rating *
                          </label>
                          <div className="flex items-center space-x-2">
                            {[1, 2, 3, 4, 5].map((rating) => (
                              <button
                                key={rating}
                                type="button"
                                onClick={() => setReviewForm({ ...reviewForm, rating })}
                                className={`p-1 ${
                                  rating <= reviewForm.rating
                                    ? 'text-yellow-400'
                                    : 'text-secondary-300'
                                } hover:text-yellow-400 transition-colors`}
                              >
                                <StarIcon className="h-6 w-6 fill-current" />
                              </button>
                            ))}
                            <span className="ml-2 text-sm text-secondary-600">
                              {reviewForm.rating} {reviewForm.rating === 1 ? 'star' : 'stars'}
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">
                            Comment (Optional)
                          </label>
                          <textarea
                            value={reviewForm.comment}
                            onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                            rows={4}
                            className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            placeholder="Share your thoughts about this product..."
                          />
                        </div>
                        <div className="flex items-center space-x-3">
                          <Button
                            onClick={handleSubmitReview}
                            disabled={isSubmittingReview}
                            loading={isSubmittingReview}
                          >
                            {myReview ? 'Update Review' : 'Submit Review'}
                          </Button>
                          {myReview && (
                            <Button
                              variant="outline"
                              onClick={handleDeleteReview}
                              disabled={isSubmittingReview}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              Delete Review
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Reviews List */}
                {isLoadingReviews ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : reviews.length === 0 ? (
                  <div className="text-center py-8 bg-secondary-50 rounded-lg border border-secondary-200">
                    <p className="text-secondary-600 mb-4">No reviews yet.</p>
                    {user && product.sellerId !== user.id && !myReview && (
                      <Button
                        variant="outline"
                        onClick={() => setShowReviewForm(true)}
                      >
                        Be the first to review
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review) => (
                      <div
                        key={review.id}
                        className="p-4 bg-secondary-50 rounded-lg border border-secondary-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
                              {review.user?.avatar ? (
                                <img
                                  src={review.user.avatar}
                                  alt={review.user.name}
                                  className="h-full w-full rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-white text-sm font-bold">
                                  {review.user?.name?.charAt(0).toUpperCase() || 'U'}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-secondary-900">
                                {review.user?.name || 'Anonymous'}
                                {review.id === myReview?.id && (
                                  <span className="ml-2 text-xs text-primary-600">(You)</span>
                                )}
                              </p>
                              <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                  <StarIcon
                                    key={i}
                                    className={`h-4 w-4 ${
                                      i < review.rating
                                        ? 'text-yellow-400 fill-current'
                                        : 'text-secondary-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm text-secondary-500">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        {review.comment && (
                          <p className="text-secondary-700 mt-2">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-8 space-y-6">
            {/* Purchase Card */}
            <Card>
              <CardContent className="p-6 space-y-6">
                {/* Price */}
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {formatCurrency(parseFloat(product.price))}
                  </div>
                  <p className="text-sm text-secondary-600">
                    One-time purchase • Instant download
                  </p>
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  {hasPurchased ? (
                    <Button
                      onClick={handleDownload}
                      className="w-full"
                      size="lg"
                      disabled={isDownloading}
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      {isDownloading ? 'Downloading...' : 'Download File'}
                    </Button>
                  ) : (
                    <Button
                      onClick={handlePurchase}
                      className="w-full"
                      size="lg"
                      disabled={!user || isPurchasing || product.status !== 'APPROVED'}
                      loading={isPurchasing}
                    >
                      <ShoppingCartIcon className="h-5 w-5 mr-2" />
                      {isPurchasing 
                        ? 'Processing...' 
                        : user 
                          ? 'Purchase & Download' 
                          : 'Login to Purchase'}
                    </Button>
                  )}
                  
                  {product.previewUrl && (
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      className="w-full"
                    >
                      <EyeIcon className="h-5 w-5 mr-2" />
                      Preview Document
                    </Button>
                  )}

                  {user && product.sellerId === user.id && !hasPurchased && (
                    <Button
                      variant="outline"
                      onClick={handleDownload}
                      className="w-full"
                      disabled={isDownloading}
                    >
                      <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                      {isDownloading ? 'Downloading...' : 'Download File (Owner)'}
                    </Button>
                  )}
                </div>

                {/* Features */}
                <div className="pt-4 border-t border-secondary-200">
                  <h4 className="font-medium text-secondary-900 mb-3">
                    What's Included
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-start text-sm text-secondary-600">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Instant download after purchase</span>
                    </div>
                    <div className="flex items-start text-sm text-secondary-600">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Lifetime access to updates</span>
                  </div>
                    <div className="flex items-start text-sm text-secondary-600">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Commercial use license included</span>
                  </div>
                    <div className="flex items-start text-sm text-secondary-600">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Full support from seller</span>
                  </div>
                  </div>
                </div>

                {/* File Info */}
                {product.media && product.media.length > 0 && (
                <div className="pt-4 border-t border-secondary-200">
                    <h4 className="font-medium text-secondary-900 mb-3">
                    File Information
                  </h4>
                    <div className="space-y-2 text-sm text-secondary-600">
                      <div className="flex justify-between">
                        <span>Files:</span>
                        <span className="font-medium">{product.media.length} items</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Last Updated:</span>
                        <span>{new Date(product.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
                )}
              </CardContent>
            </Card>

            {/* Media Gallery Card */}
            {product.media && product.media.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Media Gallery</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {product.media.slice(0, 4).map((media, index) => {
                      const imageUrl = getImageUrl(media);
                      return (
                        <button
                          key={media.id || media.key || index}
                          onClick={() => {
                            if (imageUrl) setSelectedImage(imageUrl);
                          }}
                          className="aspect-square rounded-lg overflow-hidden border-2 border-secondary-200 hover:border-primary-500 transition-colors"
                        >
                          <img
                            src={imageUrl}
                            alt={media.description || `Gallery ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=No+Image';
                            }}
                          />
                        </button>
                      );
                    })}
                  </div>
                  {product.media.length > 4 && (
                    <p className="text-center text-sm text-secondary-500 mt-3">
                      +{product.media.length - 4} more images
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmation.isOpen}
        onClose={() => setConfirmation({ ...confirmation, isOpen: false })}
        onConfirm={confirmation.onConfirm}
        title={confirmation.title}
        message={confirmation.message}
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
